import { isVisible } from "../helpers.js"
import { lockPageScroll, unlockPageScroll } from "./page_scroll_lock.js"

// The governed Modal gets focus trapping, inertness and dialog semantics free
// from `<dialog>.showModal()`. A master-detail overlay cannot:
// the same element is an ordinary inline panel above its collapse point, so it
// can neither be a dialog there nor carry dialog semantics there. This composes
// the same behaviours and engages them only while the element is actually an
// overlay - requirement-focus-trap and requirement-accessible-dialog.
//
// Escape is deliberately NOT handled here. `Modals.close_button` already carries
// `phx-hook="ModalCancel"`, so the sheet's own dismiss control is registered with
// ModalCancelManager, which owns Escape for the whole app and already picks the
// topmost visible control - including overlays rendered outside this sheet at
// layout level. A second handler here could only duplicate or contradict it.

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ")

const focusableWithin = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  )

export const setupSheetModality = (sheet, { query }) => {
  if (!sheet) return { sync: () => {}, destroy: () => {} }

  const overlayViewport = window.matchMedia(query)

  let engaged = false
  let restoreFocusTo = null

  const onKeydown = (event) => {
    if (!engaged) return

    if (event.key !== "Tab") return

    const focusable = focusableWithin(sheet)
    if (focusable.length === 0) {
      event.preventDefault()
      sheet.focus({ preventScroll: true })
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    // A sibling overlay - the layout-level confirm dialog, a nested form - can
    // legitimately hold focus above this sheet. Pulling focus back would make
    // that overlay impossible to tab through backwards, so stand aside whenever
    // focus is somewhere this sheet does not own.
    if (!sheet.contains(active)) return

    if (event.shiftKey && active === first) {
      event.preventDefault()
      last.focus({ preventScroll: true })
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus({ preventScroll: true })
    }
  }

  // Reapplied on every sync, not only on the open transition: none of these
  // attributes exist in the server markup, so a LiveView patch that morphs this
  // element - the two-phase client switch does exactly that - can strip them
  // from an overlay that stays open throughout.
  const applySemantics = () => {
    sheet.setAttribute("role", "dialog")
    sheet.setAttribute("aria-modal", "true")
    sheet.setAttribute("tabindex", "-1")

    const heading = sheet.querySelector("h1, h2, [data-modal-title]")

    if (heading) {
      if (!heading.id) heading.id = `${sheet.id || "sheet"}-title`
      sheet.setAttribute("aria-labelledby", heading.id)
      sheet.removeAttribute("aria-label")
      return
    }

    // A state that renders no heading - a deleted account's fallback - would
    // otherwise be an unnamed dialog. requirement-accessible-dialog wants a name
    // either way, so the host supplies one.
    sheet.removeAttribute("aria-labelledby")
    if (sheet.dataset.overlayLabel) {
      sheet.setAttribute("aria-label", sheet.dataset.overlayLabel)
    }
  }

  // The opener is whatever had focus when this first became an overlay. It is
  // kept across a presentation-only release, because crossing above the collapse
  // point and back is not a dismissal and must not relabel a field inside the
  // sheet as the thing to return focus to - the eventual close would then aim at
  // an element that is hidden by the time it is focused.
  const rememberOpener = () => {
    if (restoreFocusTo && restoreFocusTo.isConnected) return

    const opener = document.activeElement
    restoreFocusTo = opener && !sheet.contains(opener) ? opener : null
  }

  const engage = () => {
    applySemantics()

    if (engaged) return
    engaged = true

    rememberOpener()

    lockPageScroll()
    document.addEventListener("keydown", onKeydown, true)

    const focusable = focusableWithin(sheet)
    ;(focusable[0] || sheet).focus({ preventScroll: true })
  }

  // `restoreFocus` distinguishes a dismissal from a presentation change. Crossing
  // above the collapse point stops this being a dialog, but the same panel stays
  // on screen and the user may be typing in it - moving focus back to whatever
  // opened it would take the caret away mid-edit.
  const release = ({ restoreFocus } = { restoreFocus: true }) => {
    if (!engaged) return
    engaged = false

    sheet.removeAttribute("role")
    sheet.removeAttribute("aria-modal")
    sheet.removeAttribute("tabindex")
    sheet.removeAttribute("aria-labelledby")
    sheet.removeAttribute("aria-label")

    document.removeEventListener("keydown", onKeydown, true)
    unlockPageScroll()

    if (!restoreFocus) return

    if (restoreFocusTo && restoreFocusTo.isConnected) {
      restoreFocusTo.focus({ preventScroll: true })
    }
    restoreFocusTo = null
  }

  // The host owns open state and renders it as a data attribute, rather than
  // this inferring it from a class or from computed visibility, which a patch
  // can change under us.
  const sync = () => {
    const open = sheet.dataset.overlayOpen === "true"
    if (open && overlayViewport.matches) engage()
    else release({ restoreFocus: !open })
  }

  // engage() is idempotent, so a patch that strips the client-owned attributes
  // is repaired by the next sync rather than needing a remount.

  overlayViewport.addEventListener("change", sync)

  return {
    sync,
    destroy: () => {
      overlayViewport.removeEventListener("change", sync)
      release()
    },
  }
}
